import { useCallback, useEffect, useState, createContext, useContext, useMemo, useRef } from "react";
import { getSignalRInstance } from "./connection";
import simpleStorage from "../utils/SimpleStorage";
import { useAuth } from "../provider/authProvider";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getPositions } from "../api/positionApi";

const SignalRContext = createContext();

// CRITICAL FIX: Removed OLD useSignalR() export that was creating duplicate non-memoized connections
// All components should ONLY use useSignalRContext() from SignalRProvider
// This old hook was bypassing memoization and creating 200+ duplicate connections

export const useSignalRContext = () => useContext(SignalRContext);

export const SignalRProvider = ({ children }) => {
    const {token, clearToken} = useAuth();

    // TASK 1.3: Memoize connector instance - only recreate when token changes
    // This prevents 200+ redundant getSignalRInstance() calls throughout this file
    const connector = useMemo(() => {
        if (token && typeof token === 'string' && token.trim() !== '') {
            return getSignalRInstance(token);
        }
        return null;
    }, [token]);

    const [tickData, setTickData] = useState(() => {
        return simpleStorage.getData('tickData', []);
    });
    const [symbolsList, setSymbols] = useState(() => {
        return simpleStorage.getData('symbolsList', []);
    });
    const [activeSymbols, setActiveSymbols] = useState(() => {
        return simpleStorage.getData('activeSymbols', []);
    });
    const [subscribedSymbols, setSubscribedSymbols] = useState(() => {
        return simpleStorage.getData('subscribedSymbols', []);
    });
    const [positionsList, setPositionsList] = useState(() => {
        return simpleStorage.getData('positionsList', []);
    });
    const [ordersList, setOrdersList] = useState(() => {
        return simpleStorage.getData('ordersList', []);
    });
    const [executionsList, setExecutionsList] = useState(() => {
        return simpleStorage.getData('executionsList', []);
    });
    const [logsList, setLogsList] = useState(() => {
        return simpleStorage.getData('logsList', []);
    });
    const [tradeLogsList, setTradeLogsList] = useState(() => {
        return simpleStorage.getData('tradeLogsList', []);
    });
    const [strategyDashboardData, setStrategyDashboardData] = useState(null);

    // PHASE 5: Staleness detection for Strategy Dashboard
    const [lastStrategyDashboardUpdate, setLastStrategyDashboardUpdate] = useState(Date.now());
    const [isStrategyDashboardStale, setIsStrategyDashboardStale] = useState(false);
    const prevIsStrategyDashboardStaleRef = useRef(false);

    const [connectionStatus, setConnectionStatus] = useState("Disconnected");
    const [dataSource, setDataSource] = useState("disconnected");
    const [lastRealDataUpdate, setLastRealDataUpdate] = useState(null);
    const [connectionQuality, setConnectionQuality] = useState("unknown");
    const [isMockMode, setIsMockMode] = useState(false);
    const [mockDataWarning, setMockDataWarning] = useState(false);
    const [connectionHealth, setConnectionHealth] = useState({
        lastPing: null,
        latency: null,
        reconnectAttempts: 0,
        lastSuccessfulConnection: null
    });
    
    const [lastTickTime, setLastTickTime] = useState(Date.now());
    const [isStale, setIsStale] = useState(false);
    const prevIsStaleRef = useRef(false);

    // Account-based filtering for StrategyDashboard updates
    const selectedAccountRef = useRef(null);
    const activeStrategyIdRef = useRef(null); // Track which strategy to show data for

    const setSignalRAccount = useCallback((accountNumber) => {
        selectedAccountRef.current = accountNumber;

        // If connected, invoke SwitchAccount with new account number
        // This will trigger AccountSwitched or AccountSwitchFailed events
        if (connector && connector.getStatus() === "Connected") {
            return connector.switchAccount(accountNumber);
        }
        return Promise.resolve();
    }, [connector]);

    const setActiveStrategyId = useCallback((strategyId) => {
        activeStrategyIdRef.current = strategyId;
    }, []);

    const clearActiveStrategy = useCallback(() => {
        activeStrategyIdRef.current = null;
    }, []);

    const clearStrategyDashboard = useCallback(() => {
        setStrategyDashboardData(null);
        activeStrategyIdRef.current = null;
    }, []);

    const symbolsHandler = useCallback((data)=>{
        setDataSource("real");
        setLastRealDataUpdate(Date.now());
        setIsMockMode(false);
        setMockDataWarning(false);
        setConnectionQuality("excellent");
        
        setSymbols((previous)=>{
            const {name, id, digits, isActive, isSubscribed} = data;
            const existingIndex = previous.findIndex((i) => i.id === id);
            if (existingIndex !== -1 && previous.length > 0) {
                const updatedList = [...previous];
                updatedList[existingIndex] = { id, name, digits, isActive, isSubscribed };
                simpleStorage.setData('symbolsList', updatedList);
                return updatedList;
            } else {
                const newList = [...previous, { id, name, digits, isActive, isSubscribed }];
                simpleStorage.setData('symbolsList', newList);
                return newList;
            }
        });
    }, []);
    
    const tickHandler = useCallback((data)=>{
        setDataSource("real");
        setLastRealDataUpdate(Date.now());
        setIsMockMode(false);
        setMockDataWarning(false);
        setConnectionQuality("excellent");
        setLastTickTime(Date.now());
        setTickData((previous) => {
            const updatedList = [...previous];
            const idx = updatedList.findIndex(t => t.symbolName === data.symbolName);
            if (idx !== -1) {
                updatedList[idx] = data;
            } else {
                updatedList.push(data);
            }
            simpleStorage.setData('tickData', updatedList);
            return updatedList;
        });
    }, []);

    // Handler for symbol status updates (active/inactive)
    const symbolStatusHandler = useCallback((data) => {
        setActiveSymbols((previous) => {
            const { symbolName, isActive } = data;
            const updatedList = [...previous];
            const idx = updatedList.findIndex(s => s.symbolName === symbolName);
            
            if (isActive) {
                // Add or update active symbol
                if (idx !== -1) {
                    updatedList[idx] = { symbolName, isActive };
                } else {
                    updatedList.push({ symbolName, isActive });
                }
            } else {
                // Remove inactive symbol
                if (idx !== -1) {
                    updatedList.splice(idx, 1);
                }
            }
            
            simpleStorage.setData('activeSymbols', updatedList);
            return updatedList;
        });
    }, []);

    // Handler for symbol subscription updates
    const symbolSubscriptionHandler = useCallback((data) => {
        setSubscribedSymbols((previous) => {
            const { symbolName, isSubscribed } = data;
            const updatedList = [...previous];
            const idx = updatedList.findIndex(s => s.symbolName === symbolName);
            
            if (isSubscribed) {
                // Add or update subscribed symbol
                if (idx !== -1) {
                    updatedList[idx] = { symbolName, isSubscribed };
                } else {
                    updatedList.push({ symbolName, isSubscribed });
                }
            } else {
                // Remove unsubscribed symbol
                if (idx !== -1) {
                    updatedList.splice(idx, 1);
                }
            }
            
            simpleStorage.setData('subscribedSymbols', updatedList);
            return updatedList;
        });
    }, []);
    
    const positionHandler = useCallback((data)=>{
        setDataSource("real");
        setLastRealDataUpdate(Date.now());
        setIsMockMode(false);
        setMockDataWarning(false);
        setConnectionQuality("excellent");

        setPositionsList((previous)=>{
            // Accept both {positions: [...]} and {position: {...}}
            if (Array.isArray(data.positions)) {
                const updatedList = [...previous];
                data.positions.forEach((position) => {
                    const existingIndex = updatedList.findIndex((i) => i.id === position.id);
                    if (existingIndex !== -1) {
                        updatedList[existingIndex] = position;
                    } else {
                        updatedList.push(position);
                    }
                });
                simpleStorage.setData('positionsList', updatedList);

                // NEW: Trigger custom events for position updates
                window.dispatchEvent(new CustomEvent('positionUpdated', { detail: data.positions }));
                if (data.positions.length > 0) {
                    window.dispatchEvent(new CustomEvent('positionAdded', { detail: data.positions }));
                }

                return updatedList;
            } else if (data.position) {
                const position = data.position;
                const updatedList = [...previous];
                const existingIndex = updatedList.findIndex((i) => i.id === position.id);
                if (existingIndex !== -1) {
                    updatedList[existingIndex] = position;
                } else {
                    updatedList.push(position);
                }
                simpleStorage.setData('positionsList', updatedList);

                // NEW: Trigger custom events for position updates
                window.dispatchEvent(new CustomEvent('positionUpdated', { detail: [position] }));
                window.dispatchEvent(new CustomEvent('positionAdded', { detail: [position] }));

                return updatedList;
            } else {
                simpleStorage.setData('positionsList', []);
                return [];
            }
        });
    }, []);

    // NEW: Handler for CalculatedPositionUpdate events
    const calculatedPositionHandler = useCallback((data) => {
        setDataSource("real");
        setLastRealDataUpdate(Date.now());
        setIsMockMode(false);
        setMockDataWarning(false);
        setConnectionQuality("excellent");

        setPositionsList((previous) => {
            // Handle the CalculatedPositionUpdate format - can be direct position object or wrapped
            let position = null;
            let strategyId = null;

            // Check if data has a position property or if data itself is the position
            if (data && data.position) {
                position = data.position;
                // Extract strategyId from root level if present
                strategyId = data.strategyId || data.StrategyId || null;
            } else if (data && data.symbol && data.strategyCode) {
                // Data itself is the position object
                position = data;
                strategyId = data.strategyId || data.StrategyId || null;
            }

            if (position) {
                // Add strategyId to position object if it exists at root level
                if (strategyId !== null && strategyId !== undefined) {
                    position = { ...position, strategyId: strategyId };
                }

                const updatedList = [...previous];

                // Find existing position by symbol and strategyCode
                const existingIndex = updatedList.findIndex((i) =>
                    i.symbol === position.symbol &&
                    i.strategyCode === position.strategyCode
                );

                if (existingIndex !== -1) {
                    // If position is flat, remove it from the list
                    if (position.isFlat === true) {
                        updatedList.splice(existingIndex, 1);
                    } else {
                        updatedList[existingIndex] = position;
                    }
                } else {
                    // Only add new position if it's not flat
                    if (position.isFlat !== true) {
                        updatedList.push(position);
                    }
                }

                simpleStorage.setData('positionsList', updatedList);

                // Trigger custom events for position updates
                window.dispatchEvent(new CustomEvent('positionUpdated', { detail: [position] }));
                window.dispatchEvent(new CustomEvent('calculatedPositionUpdated', { detail: position }));

                return updatedList;
            } else {
                return previous;
            }
        });
    }, []);

    const TradelogsHandler = useCallback((logs)=>{
        if(logs?.messageType === "BusinessMessageReject"){
            toast.error(logs.message, { autoClose: 500 });
        }
        if (logs?.messageType === "ExecutionReport") {
            // Show immediately with 500ms duration
            toast.success(logs.message, { autoClose: 500 });
        }

        setTradeLogsList((prev) => {
            const updatedList = [...prev];
            updatedList.push(logs);
            simpleStorage.setData('tradeLogsList', updatedList);
            return updatedList;
        });
    }, []);

    const strategyDashboardHandler = useCallback((data) => {
        // PHASE 1: Logging for observability
        console.group('📊 StrategyDashboard Update');

        // PHASE 2: Validation
        // Check 1: Data exists and is object
        if (!data || typeof data !== 'object') {
            console.error('❌ VALIDATION FAILED: Invalid StrategyDashboard data');
            console.error('Received:', data);
            console.groupEnd();
            toast.error('Invalid strategy data received', {
                position: 'top-right',
                autoClose: 3000,
                toastId: 'invalid-strategy-data'
            });
            return; // Don't update state with bad data
        }

        // Check 1.5: Account-based filtering
        const strategyAccountNumber = data.accountNumber;
        const currentSelectedAccount = selectedAccountRef.current;

        if (currentSelectedAccount && strategyAccountNumber) {
            // Convert both to strings for safe comparison
            const strategyAccNum = String(strategyAccountNumber);
            const selectedAccNum = String(currentSelectedAccount);

            if (strategyAccNum !== selectedAccNum) {
                console.warn('⚠️ ACCOUNT MISMATCH: Rejecting StrategyDashboard update');
                console.warn('Strategy Account:', strategyAccNum, '| Selected Account:', selectedAccNum);
                console.groupEnd();
                return; // Ignore data from strategies with different account numbers
            }
        }

        // Check 1.6: Strategy ID filtering (only show data for active strategy)
        const strategyId = data.id || data.strategyId || data.StrategyId || data.strategyID;
        const currentActiveStrategyId = activeStrategyIdRef.current;

        // If no active strategy is set, accept and set this as active
        if (!currentActiveStrategyId && strategyId) {
            activeStrategyIdRef.current = String(strategyId);
            console.warn('✅ No active strategy - Setting as active:', strategyId);
        }
        // If active strategy is set, only accept data from that strategy
        else if (currentActiveStrategyId && strategyId) {
            const dataStrategyId = String(strategyId);
            const activeStrategyId = String(currentActiveStrategyId);

            if (dataStrategyId !== activeStrategyId) {
                console.warn('⚠️ STRATEGY ID MISMATCH: Rejecting StrategyDashboard update');
                console.warn('Data Strategy ID:', dataStrategyId, '| Active Strategy ID:', activeStrategyId);
                console.groupEnd();
                return; // Ignore data from other strategies on same account
            }
        }

        // Check 2: Has strategy ID
        if (!strategyId) {
            console.warn('⚠️ VALIDATION WARNING: StrategyDashboard missing strategy ID');
            console.warn('Data:', data);
            // Allow to continue but warn
        }

        // Check 3: If marketData exists, validate it has minimum required fields
        if (data.marketData && typeof data.marketData === 'object') {
            const hasRequiredPrices = data.marketData.futuresBid !== undefined &&
                                     data.marketData.futuresAsk !== undefined;
            if (!hasRequiredPrices) {
                console.warn('⚠️ VALIDATION WARNING: MarketData incomplete, keeping previous data');
                console.warn('MarketData received:', data.marketData);
                console.groupEnd();
                return; // Keep old data instead of showing blanks
            }
        }


        if (data) {
            // Data validation passed
        }

        // PHASE 3: Add metadata enrichment
        const enrichedData = {
            ...data,
            _metadata: {
                receivedAt: Date.now(),
                receivedAtISO: new Date().toISOString(),
                source: 'SignalR:StrategyDashboard',
                dataAge: 0 // Will be calculated dynamically
            }
        };


        // PHASE 4: Data diffing to prevent unnecessary re-renders
        setStrategyDashboardData((prevData) => {
            // If no previous data, always update
            if (!prevData) {
                console.groupEnd();

                // PHASE 5: Update staleness tracking for first data
                setLastStrategyDashboardUpdate(Date.now());
                setIsStrategyDashboardStale(false);

                return enrichedData;
            }

            // Compare data (excluding metadata for comparison)
            const prevDataWithoutMeta = { ...prevData };
            delete prevDataWithoutMeta._metadata;

            const newDataWithoutMeta = { ...enrichedData };
            delete newDataWithoutMeta._metadata;

            const prevString = JSON.stringify(prevDataWithoutMeta);
            const newString = JSON.stringify(newDataWithoutMeta);

            if (prevString === newString) {
                console.groupEnd();
                return prevData; // Return previous data unchanged (no re-render)
            }

            console.groupEnd();

            // PHASE 5: Update staleness tracking
            setLastStrategyDashboardUpdate(Date.now());
            setIsStrategyDashboardStale(false);

            return enrichedData;
        });
    }, []);

    const logsHandler = useCallback((logs) => {
        setLogsList((prev) => {
            const updatedList = [...prev];
            updatedList.push(logs);
            simpleStorage.setData('logsList', updatedList);
            return updatedList;
        });
    }, []);
    
    const executionHandler = useCallback((data)=>{
        setExecutionsList((prev) => {
            const updatedList = [...prev];
            updatedList.push(data);
            simpleStorage.setData('executionsList', updatedList);
            return updatedList;
        });
    }, []);
    
    const orderExecutionHandler = useCallback((data) => {
        let order, type;
        if (data.execution && data.execution.order) {
            order = data.execution.order;
            type = data.execution.type;
        } else if (data.execution) {
            order = data.execution;
            type = data.execution.type || null;
        } else {
            return;
        }
        // Normalize order object to ensure all required fields exist
        const normalizedOrder = {
            id: order.id || order.ID || order.Id || order.orderId || order.OrderId || '',
            clOrderId: order.clOrderId || order.ClOrderId || order.clOrdId || order.ClOrdId || '',
            time: order.time || order.Time || '',
            expireTime: order.expireTime || order.ExpireTime || '',
            timeInForce: order.timeInForce || order.TimeInForce || '',
            type: order.type || order.Type || '',
            symbolId: order.symbolId || order.SymbolId || '',
            symbolName: order.symbolName || order.SymbolName || '',
            targetPrice: order.targetPrice || order.TargetPrice || '',
            volume: order.volume || order.Volume || '',
            tradeSide: order.tradeSide || order.TradeSide || '',
            stopLoss: order.stopLoss || order.StopLoss || '',
            takeProfit: order.takeProfit || order.TakeProfit || '',
        };
        
        setOrdersList((previous) => {
            const existingIndex = previous ? previous.findIndex((i) => i && i?.id === normalizedOrder.id) : -1;
            const updatedList = [...previous];
            if (existingIndex !== -1) {
                updatedList.splice(existingIndex, 1);
            }
            if (type !== '4' && type !== '8' && type !== 'C' && type !== 'F') {
                updatedList.push(normalizedOrder);
            }
            simpleStorage.setData('ordersList', updatedList);
            return updatedList;
        });

        // NEW: Trigger custom event when order is executed
        // This will help the Position component know when to refresh
        if (type === '2' || type === 'F') { // Order filled or executed
            window.dispatchEvent(new CustomEvent('orderExecuted', { detail: normalizedOrder }));
        }

        // ALWAYS trigger position refresh when any order is processed
        window.dispatchEvent(new CustomEvent('positionUpdated', { detail: normalizedOrder }));
    }, []);
    
    const clear = useCallback(() => {
        setTickData([]);
        setSymbols([]);
        setPositionsList([]);
        setOrdersList([]);
        setExecutionsList([]);
        setLogsList([]);
        setTradeLogsList([]);
        
        // Clear storage
        simpleStorage.setData('tickData', []);
        simpleStorage.setData('symbolsList', []);
        simpleStorage.setData('positionsList', []);
        simpleStorage.setData('ordersList', []);
        simpleStorage.setData('executionsList', []);
        simpleStorage.setData('logsList', []);
        simpleStorage.setData('tradeLogsList', []);
    }, []);

    // Load initial positions from API (call this after login)
    const loadInitialPositions = useCallback(async () => {
        if (!token) {
            return;
        }

        try {
            const positions = await getPositions(token);

            if (positions && Array.isArray(positions)) {
                setPositionsList(positions);
                simpleStorage.setData('positionsList', positions);
            }
        } catch (error) {
            // Don't throw - just silently fail, positions will update via SignalR
            console.error('Error loading positions:', error);
        }
    }, [token]);

    const checkConnection = useCallback(()=>{
        if (!connector) return false;
        return connector.getStatus() === "Connected";
    }, [connector]);
    
    // Setup SignalR connection and event handlers
    useEffect(() => {
        if (connector) {
            // CRITICAL FIX: Use EVENT-DRIVEN connection status tracking instead of polling
            // These SignalR events provide all connection state changes
            connector.connection.onreconnecting((error) => {
                setConnectionStatus("Reconnecting");
                setDataSource("mock");
                setIsMockMode(true);
                setConnectionQuality("poor");
            });

            connector.connection.onreconnected((connectionId) => {
                setConnectionStatus("Connected");
                setDataSource("real");
                setIsMockMode(false);
                setMockDataWarning(false);
                setConnectionQuality("excellent");
                setConnectionHealth(prev => ({
                    ...prev,
                    lastSuccessfulConnection: Date.now(),
                    reconnectAttempts: 0
                }));
            });

            connector.connection.onclose((error) => {
                setConnectionStatus("Disconnected");
                setDataSource("disconnected");
                setIsMockMode(true);
                setMockDataWarning(true);
                setConnectionQuality("disconnected");
            });

            connector.connection.on("SymbolQuotes", tickHandler);
            connector.connection.on("SymbolReport", symbolsHandler);
            connector.connection.on("PositionReport", positionHandler);
            connector.connection.on("CalculatedPositionUpdate", calculatedPositionHandler);
            connector.connection.on("ExecutionReport", executionHandler);
            connector.connection.on("OrderReport", orderExecutionHandler);
            connector.connection.on("FixLogs", logsHandler);
            connector.connection.on("TradeLogs", TradelogsHandler);
            connector.connection.on("SymbolStatusUpdate", symbolStatusHandler);
            connector.connection.on("SymbolSubscriptionUpdate", symbolSubscriptionHandler);
            connector.connection.on("StrategyDashboard", strategyDashboardHandler);

            // NEW: Account switch event handlers
            connector.connection.on("AccountSwitched", (accountNumber) => {
                console.log(`✅ Account switched successfully to: ${accountNumber}`);
                toast.success(`Switched to account ${accountNumber}`, {
                    position: "top-right",
                    autoClose: 500
                });
            });

            connector.connection.on("AccountSwitchFailed", (reason) => {
                console.error(`❌ Account switch failed: ${reason}`);
                toast.error(`Failed to switch account: ${reason}`, {
                    position: "top-right",
                    autoClose: 5000
                });
            });

            connector.connection.on("Disconnected", (reason) => {
                console.warn(`⚠️ Disconnected from server: ${reason}`);
                setConnectionStatus("Disconnected");
                setDataSource("disconnected");
                setConnectionQuality("disconnected");

                if (reason === 'Invalid token') {
                    toast.error("Session expired. Please login again.", {
                        position: "top-right",
                        autoClose: 5000
                    });
                    // Token expired - trigger logout
                    setTimeout(() => {
                        clearToken();
                        window.location.href = '/login';
                    }, 2000);
                } else if (reason === 'Unauthorized account') {
                    toast.error("You do not have permission to access this account", {
                        position: "top-right",
                        autoClose: 5000
                    });
                    // Unauthorized - clear account and reload
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                } else {
                    toast.warning(`Disconnected: ${reason}`, {
                        position: "top-right",
                        autoClose: 5000
                    });
                }
            });

            // CRITICAL FIX: Removed waitForConnection() polling loop
            // It was checking connection status every 100ms-2s causing excessive activity
            // Now using event-driven approach via onreconnected/onclose events above
            // Pass account number for backend filtering (may be null on initial connection)
            connector.connect(token, selectedAccountRef.current);
        } else {
            setIsMockMode(true);
            setDataSource("disconnected");
            setConnectionQuality("disconnected");
            setConnectionStatus("Disconnected");
        }

        // CRITICAL FIX: Remove all event listeners on cleanup to prevent duplication
        return () => {
            if (connector) {
                connector.connection.off("SymbolQuotes", tickHandler);
                connector.connection.off("SymbolReport", symbolsHandler);
                connector.connection.off("PositionReport", positionHandler);
                connector.connection.off("CalculatedPositionUpdate", calculatedPositionHandler);
                connector.connection.off("ExecutionReport", executionHandler);
                connector.connection.off("OrderReport", orderExecutionHandler);
                connector.connection.off("FixLogs", logsHandler);
                connector.connection.off("TradeLogs", TradelogsHandler);
                connector.connection.off("SymbolStatusUpdate", symbolStatusHandler);
                connector.connection.off("SymbolSubscriptionUpdate", symbolSubscriptionHandler);
                connector.connection.off("StrategyDashboard", strategyDashboardHandler);
                connector.connection.off("AccountSwitched");
                connector.connection.off("AccountSwitchFailed");
                connector.connection.off("Disconnected");
            }
        };
    }, [connector]); // CRITICAL FIX: Only depend on connector (which depends on token)
    
    // Listen for symbol status updates from EditSymbolModal
    useEffect(() => {
        const handleSymbolUpdate = (event) => {
            const { symbol, action } = event.detail;

            if (action === 'update' && symbol) {
                // Update active symbols list based on isActive status
                setActiveSymbols((previous) => {
                    const updatedList = [...previous];
                    const idx = updatedList.findIndex(s => s.symbolName === symbol.name);
                    
                    if (symbol.isActive) {
                        // Add or update active symbol
                        if (idx !== -1) {
                            updatedList[idx] = { symbolName: symbol.name, isActive: true };
                        } else {
                            updatedList.push({ symbolName: symbol.name, isActive: true });
                        }
                    } else {
                        // Remove inactive symbol
                        if (idx !== -1) {
                            updatedList.splice(idx, 1);
                        }
                    }
                    
                    simpleStorage.setData('activeSymbols', updatedList);
                    return updatedList;
                });

                // Update subscribed symbols list based on isSubscribed status
                setSubscribedSymbols((previous) => {
                    const updatedList = [...previous];
                    const idx = updatedList.findIndex(s => s.symbolName === symbol.name);
                    
                    if (symbol.isSubscribed) {
                        // Add or update subscribed symbol
                        if (idx !== -1) {
                            updatedList[idx] = { symbolName: symbol.name, isSubscribed: true };
                        } else {
                            updatedList.push({ symbolName: symbol.name, isSubscribed: true });
                        }
                    } else {
                        // Remove unsubscribed symbol
                        if (idx !== -1) {
                            updatedList.splice(idx, 1);
                        }
                    }
                    
                    simpleStorage.setData('subscribedSymbols', updatedList);
                    return updatedList;
                });
            }
        };

        window.addEventListener('symbolUpdated', handleSymbolUpdate);
        return () => {
            window.removeEventListener('symbolUpdated', handleSymbolUpdate);
        };
    }, []);

    // CRITICAL FIX: Removed manual reconnection handlers
    // SignalR's .withAutomaticReconnect() handles ALL reconnection automatically
    // Manual reconnection was causing 200+ duplicate connection attempts
    // The built-in reconnection events (onreconnecting, onreconnected, onclose) provide all needed feedback
    
    // Staleness detection effect
    useEffect(() => {
        const interval = setInterval(() => {
            if (Date.now() - lastTickTime > 10000) { // 10 seconds
                if (!isStale) {
                    setIsStale(true);
                }
            } else {
                if (isStale) {
                    setIsStale(false);
                }
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [lastTickTime, isStale]);
    
    // Show toast only on transition to stale
    useEffect(() => {
        if (isStale && !prevIsStaleRef.current) {
            setDataSource('disconnected');
            setConnectionQuality('disconnected');
            toast.warning('⚠️ Real-time data connection lost. Displaying cached data.', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                toastId: 'connection-lost'
            });
        } else if (!isStale && prevIsStaleRef.current) {
            setDataSource('real');
            setConnectionQuality('excellent');
        }
        prevIsStaleRef.current = isStale;
    }, [isStale]);

    // PHASE 5: Strategy Dashboard staleness detection
    useEffect(() => {
        const interval = setInterval(() => {
            const timeSinceLastUpdate = Date.now() - lastStrategyDashboardUpdate;

            if (timeSinceLastUpdate > 30000) { // 30 seconds
                if (!isStrategyDashboardStale) {
                    setIsStrategyDashboardStale(true);
                    console.warn('⚠️ Strategy Dashboard data is stale (no update for 30+ seconds)');
                }
            } else {
                if (isStrategyDashboardStale) {
                    setIsStrategyDashboardStale(false);
                }
            }
        }, 5000); // Check every 5 seconds

        return () => clearInterval(interval);
    }, [lastStrategyDashboardUpdate, isStrategyDashboardStale]);

    // Show toast on Strategy Dashboard staleness transition
    useEffect(() => {
        if (isStrategyDashboardStale && !prevIsStrategyDashboardStaleRef.current) {
            toast.warning('⚠️ Strategy dashboard data is stale. Last update: ' +
                Math.floor((Date.now() - lastStrategyDashboardUpdate) / 1000) + 's ago', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                toastId: 'strategy-dashboard-stale'
            });
        } else if (!isStrategyDashboardStale && prevIsStrategyDashboardStaleRef.current) {
            toast.success('✅ Strategy dashboard data is fresh', {
                position: 'top-right',
                autoClose: 2000,
                toastId: 'strategy-dashboard-fresh'
            });
        }
        prevIsStrategyDashboardStaleRef.current = isStrategyDashboardStale;
    }, [isStrategyDashboardStale, lastStrategyDashboardUpdate]);
    
    const connect = useCallback((_token, accountNumber = null) => {
        if (!connector) {
            return;
        }

        // Check if already connected to prevent multiple connections
        if (connector.getStatus() === "Connected") {
            return;
        }

        // Pass accountNumber to connector for backend filtering
        const accNum = accountNumber || selectedAccountRef.current;
        connector.connect(_token || token, accNum);
    }, [connector, token]);
    
    
    const isConnected = useCallback(()=>{
        if (!connector) return false;
        return (connector.getStatus() === "Connected");
    }, [connector])
    
    const disconnect = useCallback((_token) => {
        if (!connector) return;
        connector.disconnect(_token || token);
        setConnectionStatus("Disconnected");
        clearToken();
        clear();
    }, [connector, token, clearToken, clear]);

    const sendPing = useCallback((_token) => {
        if (!connector) return;
        connector.newMessage("Ping");
    }, [connector]);

    const sendCustomMessage = useCallback((message, _token) => {
        if (!connector) return;
        connector.newMessage(message);
    }, [connector]);

    const sendOrderRequest = useCallback((_token, order, accountNumber = null)=>{
        if (!connector) {
            return;
        }
        connector.sendOrder(_token || token, order, accountNumber);
    }, [connector, token]);

    const sendSpreadOrderRequest = useCallback((_token, order1, order2, accountNumber = null)=>{
        if (!connector) {
            return;
        }
        connector.sendSpreadOrder(_token || token, order1, order2, accountNumber);
    }, [connector, token]);

    const sendPositionCloseRequest = useCallback((positionId)=>{
        if (!connector) return;
        connector.sendPositionClose(positionId);
    }, [connector]);

    const sendCloseOrderRequest = useCallback((_token, orderId, clOrdId, side = '1') => {
        if (!connector) return;
        return connector.sendCloseOrder(_token || token, orderId, clOrdId, side);
    }, [connector, token]);

    const sendClosePositionRequest = useCallback((_token, params) => {
        if (!connector) return;
        return connector.sendClosePosition(_token || token, params);
    }, [connector, token]);

    const sendOrderAmmendRequest = useCallback((_token, order)=>{
        if (!connector) return;
        connector.sendOrderAmmend(_token || token, order);
    }, [connector, token]);

    const sendNewOrderRequest = useCallback((_token, order, accountNumber = null) => {
        if (!connector) {
            return;
        }
        connector.sendOrder(_token || token, order, accountNumber);
    }, [connector, token]);
    
    const value = useMemo(() => ({
        tickData, // Use original tickData without filtering for now
        symbolsList,
        activeSymbols,
        subscribedSymbols,
        positionsList,
        ordersList,
        executionsList,
        logsList,
        tradeLogsList,
        strategyDashboardData,
        // PHASE 5: Export staleness detection state
        isStrategyDashboardStale,
        lastStrategyDashboardUpdate,
        connectionStatus,
        dataSource,
        isMockMode,
        mockDataWarning,
        connectionQuality,
        lastRealDataUpdate,
        connectionHealth,
        lastTickTime,
        isStale,

        // Methods
        connect,
        disconnect,
        isConnected: checkConnection,
        clear,
        loadInitialPositions,
        sendPing,
        sendCustomMessage,
        sendOrderRequest,
        sendSpreadOrderRequest,
        sendPositionCloseRequest,
        sendCloseOrderRequest,
        sendClosePositionRequest,
        sendOrderAmmendRequest,
        sendNewOrderRequest,
        // Account & Strategy filtering
        setSignalRAccount,
        setActiveStrategyId,
        clearActiveStrategy,
        clearStrategyDashboard
    }), [
        // State dependencies
        tickData,
        symbolsList,
        activeSymbols,
        subscribedSymbols,
        positionsList,
        ordersList,
        executionsList,
        logsList,
        tradeLogsList,
        strategyDashboardData,
        // PHASE 5: Staleness detection dependencies
        isStrategyDashboardStale,
        lastStrategyDashboardUpdate,
        connectionStatus,
        dataSource,
        isMockMode,
        mockDataWarning,
        connectionQuality,
        lastRealDataUpdate,
        connectionHealth,
        lastTickTime,
        isStale,

        // Method dependencies
        connect,
        disconnect,
        checkConnection,
        clear,
        sendPing,
        sendCustomMessage,
        sendOrderRequest,
        sendSpreadOrderRequest,
        sendPositionCloseRequest,
        sendCloseOrderRequest,
        sendClosePositionRequest,
        sendOrderAmmendRequest,
        checkConnection,
        loadInitialPositions,
        sendNewOrderRequest,
        setSignalRAccount,
        setActiveStrategyId,
        clearActiveStrategy,
        clearStrategyDashboard,
        lastTickTime,
        isStale
    ]);

    return (
        <SignalRContext.Provider value={value}>
            {children}
        </SignalRContext.Provider>
    );
};